import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import {
  ChatMessage,
  CommunicationContact,
  CommunicationDirectoryGroup,
  ConversationSummary,
} from '../models/communication-api.model';
import { ApiUserRole } from '../models/auth-api.model';
import { CommunicationApiService } from './communication-api.service';
import { CommunicationSocketService } from './communication-socket.service';
import { ChatGoalsService } from './chat-goals.service';
import { SessionService } from './session.service';

const AVATAR_COLORS = ['#4D8CFF', '#3CC7B6', '#FF6B5E', '#FFC95A', '#8F7CFF', '#5AD1A6'];

export interface ChatPerson {
  /** Conversation id when known; otherwise peer user id for new threads. */
  id: string;
  peerUserId: string;
  conversationId: string | null;
  name: string;
  initials: string;
  color: string;
  /** Profile avatar path under /uploads when set. */
  avatarUrl?: string | null;
  online: boolean;
  role: string;
  status: string;
  unread: number;
  preview: string;
  time: string;
}

/** A course and the people the viewer shares it with. */
export interface ChatDirectoryGroup {
  courseId: string;
  courseTitle: string;
  courseColor: string | null;
  people: ChatPerson[];
}

/**
 * Signal store for the messages screens (student + teacher).
 * Loads contacts/conversations over REST and stays live via Socket.IO.
 */
@Injectable({ providedIn: 'root' })
export class MessagingStore {
  private readonly api = inject(CommunicationApiService);
  private readonly socket = inject(CommunicationSocketService);
  private readonly chatGoals = inject(ChatGoalsService);
  private readonly session = inject(SessionService);

  readonly loading = signal(false);
  readonly error = signal('');
  readonly contacts = signal<CommunicationContact[]>([]);
  readonly conversations = signal<ConversationSummary[]>([]);
  readonly selectedId = signal<string | null>(null);
  readonly messagesByConversation = signal<Record<string, ChatMessage[]>>({});
  readonly onlineIds = signal<ReadonlySet<string>>(new Set());
  readonly peerTyping = signal(false);
  readonly draft = signal('');
  readonly query = signal('');

  readonly directoryOpen = signal(false);
  readonly directoryLoading = signal(false);
  readonly directoryError = signal('');
  readonly directoryQuery = signal('');
  private readonly directoryGroups = signal<CommunicationDirectoryGroup[]>([]);

  private wired = false;
  private lastHandledMessageId: string | null = null;
  private lastPresenceKey: string | null = null;
  private lastTypingKey: string | null = null;
  private lastConversationUpdateKey: string | null = null;
  private lastGoalsUpdateKey: string | null = null;
  private readonly unsubscribers: Array<() => void> = [];

  constructor() {
    // AuthService injects this store, so every authenticated web session
    // applies socket events even before the messages screen mounts.
    this.wireRealtime();
  }

  readonly people = computed<ChatPerson[]>(() => {
    const q = this.query().trim().toLowerCase();
    const online = this.onlineIds();
    const conversations = this.conversations();
    const byPeer = new Map(conversations.map((c) => [c.peer.userId, c]));

    const fromConversations = conversations.map((conversation) =>
      this.toPersonFromConversation(conversation, online),
    );

    const contactOnly = this.contacts()
      .filter((contact) => !byPeer.has(contact.userId))
      .map((contact) => this.toPersonFromContact(contact, online));

    const all = [...fromConversations, ...contactOnly];
    return all.filter(
      (person) =>
        !q ||
        person.name.toLowerCase().includes(q) ||
        person.role.toLowerCase().includes(q),
    );
  });

  readonly active = computed<ChatPerson | null>(() => {
    const id = this.selectedId();
    if (!id) return this.people()[0] ?? null;
    return this.people().find((p) => p.id === id) ?? this.people()[0] ?? null;
  });

  readonly thread = computed<ChatMessage[]>(() => {
    const active = this.active();
    if (!active?.conversationId) return [];
    return this.messagesByConversation()[active.conversationId] ?? [];
  });

  readonly meId = computed(() => this.session.user()?.id ?? '');

  readonly directory = computed<ChatDirectoryGroup[]>(() => {
    const q = this.directoryQuery().trim().toLowerCase();
    const online = this.onlineIds();
    return this.directoryGroups()
      .map((group) => ({
        courseId: group.courseId,
        courseTitle: group.courseTitle,
        courseColor: group.courseColor,
        people: group.people
          .map((contact) => this.toPersonFromContact(contact, online))
          .filter((person) => !q || person.name.toLowerCase().includes(q)),
      }))
      .filter((group) => group.people.length > 0 || !q);
  });

  readonly directoryCount = computed(() =>
    this.directory().reduce((total, group) => total + group.people.length, 0),
  );

  async bootstrap(): Promise<void> {
    this.wireRealtime();
    // Socket is owned by AuthService (login / restore / logout); ensure it is up.
    this.socket.connect();
    this.loading.set(true);
    this.error.set('');
    try {
      const [contacts, conversations] = await Promise.all([
        firstValueFrom(this.api.contacts()),
        firstValueFrom(this.api.conversations()),
      ]);
      this.contacts.set(contacts);
      this.conversations.set(conversations.items);
      contacts.forEach((c) => {
        if (c.online) {
          this.onlineIds.update((set) => new Set([...set, c.userId]));
        }
      });

      const first = this.people()[0];
      if (first) {
        await this.select(first);
      }
    } catch (err) {
      this.error.set(err instanceof Error ? err.message : 'Could not load messages');
    } finally {
      this.loading.set(false);
    }
  }

  /** Open the course directory popup, loading it on first use. */
  async openDirectory(): Promise<void> {
    this.directoryOpen.set(true);
    this.directoryQuery.set('');
    this.directoryLoading.set(true);
    this.directoryError.set('');
    try {
      this.directoryGroups.set(await firstValueFrom(this.api.directory()));
    } catch (err) {
      this.directoryError.set(
        err instanceof Error ? err.message : 'Could not load your courses',
      );
    } finally {
      this.directoryLoading.set(false);
    }
  }

  closeDirectory(): void {
    this.directoryOpen.set(false);
    this.directoryQuery.set('');
  }

  setDirectoryQuery(value: string): void {
    this.directoryQuery.set(value);
  }

  /** Open (or create) the thread with someone picked from the directory. */
  async startFromDirectory(person: ChatPerson): Promise<void> {
    this.closeDirectory();
    const known = this.people().find((p) => p.peerUserId === person.peerUserId);
    await this.select(known ?? person);
  }

  /** Clear local chat state on logout — does not touch the socket (AuthService owns that). */
  reset(): void {
    this.loading.set(false);
    this.error.set('');
    this.contacts.set([]);
    this.conversations.set([]);
    this.selectedId.set(null);
    this.messagesByConversation.set({});
    this.onlineIds.set(new Set());
    this.peerTyping.set(false);
    this.draft.set('');
    this.query.set('');
    this.directoryOpen.set(false);
    this.directoryLoading.set(false);
    this.directoryError.set('');
    this.directoryQuery.set('');
    this.directoryGroups.set([]);
    this.lastHandledMessageId = null;
    this.lastPresenceKey = null;
    this.lastTypingKey = null;
    this.lastConversationUpdateKey = null;
    this.lastGoalsUpdateKey = null;
  }

  async select(person: ChatPerson): Promise<void> {
    this.selectedId.set(person.id);
    this.peerTyping.set(false);
    this.draft.set('');

    let conversationId = person.conversationId;
    if (!conversationId) {
      const started = await firstValueFrom(
        this.api.startConversation({ peerUserId: person.peerUserId }),
      );
      this.upsertConversation(started);
      conversationId = started.id;
      this.selectedId.set(started.id);
    }

    this.socket.joinConversation(conversationId);
    await this.loadMessages(conversationId);
    await firstValueFrom(this.api.markRead(conversationId));
    this.conversations.update((list) =>
      list.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0 } : c)),
    );
  }

  async send(): Promise<void> {
    const text = this.draft().trim();
    const active = this.active();
    if (!text || !active) return;

    let conversationId = active.conversationId;
    if (!conversationId) {
      const started = await firstValueFrom(
        this.api.startConversation({ peerUserId: active.peerUserId }),
      );
      this.upsertConversation(started);
      conversationId = started.id;
      this.selectedId.set(started.id);
      this.socket.joinConversation(conversationId);
    }

    const clientMessageId =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `c-${Date.now()}`;

    this.draft.set('');
    this.socket.stopTyping(conversationId);

    const ack = await this.socket.sendMessage(conversationId, text, clientMessageId);
    if (ack.ok && ack.chatMessage) {
      this.appendMessage(ack.chatMessage);
      return;
    }

    // Fallback to REST if socket is unavailable.
    const message = await firstValueFrom(
      this.api.sendMessage(conversationId, { body: text, clientMessageId }),
    );
    this.appendMessage(message);
  }

  setQuery(value: string): void {
    this.query.set(value);
  }

  setDraft(value: string): void {
    this.draft.set(value);
    const active = this.active();
    if (!active?.conversationId) return;
    if (value.trim()) {
      this.socket.startTyping(active.conversationId);
    } else {
      this.socket.stopTyping(active.conversationId);
    }
  }

  private wireRealtime(): void {
    if (this.wired) return;
    this.wired = true;

    this.unsubscribers.push(
      this.socket.onMessage((message) => {
        if (message.id === this.lastHandledMessageId) {
          return;
        }
        this.lastHandledMessageId = message.id;
        this.appendMessage(message);
      }),
      this.socket.onPresence((presence) => {
        const key = `${presence.userId}:${presence.online}`;
        if (key === this.lastPresenceKey) {
          return;
        }
        this.lastPresenceKey = key;
        this.onlineIds.update((set) => {
          const next = new Set(set);
          if (presence.online) next.add(presence.userId);
          else next.delete(presence.userId);
          return next;
        });
      }),
      this.socket.onTyping((typing) => {
        const key = `${typing.conversationId}:${typing.userId}:${typing.typing}`;
        if (key === this.lastTypingKey) {
          return;
        }
        this.lastTypingKey = key;
        const active = this.active();
        if (
          active?.conversationId === typing.conversationId &&
          typing.userId !== this.meId()
        ) {
          this.peerTyping.set(typing.typing);
        }
      }),
      this.socket.onConversationUpdated((updated) => {
        const key = `${updated.conversationId}:${updated.lastMessageAt}`;
        if (key === this.lastConversationUpdateKey) {
          return;
        }
        this.lastConversationUpdateKey = key;
        this.conversations.update((list) =>
          list.map((c) =>
            c.id === updated.conversationId
              ? {
                  ...c,
                  lastMessageAt: updated.lastMessageAt,
                  lastMessagePreview: updated.lastMessagePreview,
                  unreadCount:
                    updated.senderId === this.meId() ||
                    this.active()?.conversationId === updated.conversationId
                      ? 0
                      : c.unreadCount + 1,
                }
              : c,
          ),
        );
      }),
      this.socket.onGoalsUpdated((goalsUpdate) => {
        const key = `${goalsUpdate.conversationId}:${goalsUpdate.studentUserId}:${goalsUpdate.goals.length}:${goalsUpdate.goals.map((goal) => goal.id).join(',')}`;
        if (key === this.lastGoalsUpdateKey) {
          return;
        }
        this.lastGoalsUpdateKey = key;
        this.chatGoals.applyRemoteGoals(goalsUpdate.studentUserId, goalsUpdate.goals);
      }),
    );
  }

  /**
   * @deprecated Store now subscribes directly in the constructor. Kept so
   * existing messages-screen effects remain harmless.
   */
  pumpRealtime(): void {
    // no-op — live updates are applied via socket fan-out handlers.
  }

  private async loadMessages(conversationId: string): Promise<void> {
    const page = await firstValueFrom(this.api.messages(conversationId));
    this.messagesByConversation.update((all) => {
      const live = all[conversationId] ?? [];
      const byId = new Map(page.items.map((message) => [message.id, message]));
      for (const message of live) {
        if (!byId.has(message.id)) {
          byId.set(message.id, message);
        }
      }
      const merged = [...byId.values()].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
      );
      return {
        ...all,
        [conversationId]: merged,
      };
    });
  }

  private appendMessage(message: ChatMessage): void {
    this.messagesByConversation.update((all) => {
      const existing = all[message.conversationId] ?? [];
      if (existing.some((m) => m.id === message.id)) {
        return all;
      }
      if (
        message.clientMessageId &&
        existing.some((m) => m.clientMessageId === message.clientMessageId)
      ) {
        return all;
      }
      return {
        ...all,
        [message.conversationId]: [...existing, message],
      };
    });

    this.conversations.update((list) => {
      const found = list.find((c) => c.id === message.conversationId);
      if (!found) return list;
      return [
        {
          ...found,
          lastMessageAt: message.createdAt,
          lastMessagePreview: message.body,
        },
        ...list.filter((c) => c.id !== message.conversationId),
      ];
    });
  }

  private upsertConversation(conversation: ConversationSummary): void {
    this.conversations.update((list) => {
      const without = list.filter((c) => c.id !== conversation.id);
      return [conversation, ...without];
    });
  }

  private toPersonFromConversation(
    conversation: ConversationSummary,
    online: ReadonlySet<string>,
  ): ChatPerson {
    const name = conversation.peer.name?.trim() || conversation.peer.email;
    const isOnline = online.has(conversation.peer.userId);
    return {
      id: conversation.id,
      peerUserId: conversation.peer.userId,
      conversationId: conversation.id,
      name,
      initials: this.initials(name),
      color: this.colorFor(conversation.peer.userId),
      avatarUrl: conversation.peer.avatarUrl ?? null,
      online: isOnline,
      role: this.roleLabel(conversation.peer.roles),
      status: isOnline ? 'Online now' : 'Offline',
      unread: conversation.unreadCount,
      preview: conversation.lastMessagePreview
        ? conversation.lastMessagePreview
        : 'Say hello to start the conversation',
      time: this.formatTime(conversation.lastMessageAt),
    };
  }

  private toPersonFromContact(
    contact: CommunicationContact,
    online: ReadonlySet<string>,
  ): ChatPerson {
    const name = contact.name?.trim() || contact.email;
    const isOnline = online.has(contact.userId) || !!contact.online;
    return {
      id: contact.conversationId ?? `peer:${contact.userId}`,
      peerUserId: contact.userId,
      conversationId: contact.conversationId,
      name,
      initials: this.initials(name),
      color: this.colorFor(contact.userId),
      avatarUrl: contact.avatarUrl ?? null,
      online: isOnline,
      role: this.roleLabel(contact.roles),
      status: isOnline ? 'Online now' : 'Offline',
      unread: 0,
      preview: 'Start a new conversation',
      time: '',
    };
  }

  private roleLabel(roles: ApiUserRole[]): string {
    if (roles.includes(ApiUserRole.Teacher)) return 'Teacher';
    if (roles.includes(ApiUserRole.Student)) return 'Student';
    return 'Member';
  }

  private initials(name: string): string {
    const parts = name.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }

  private colorFor(userId: string): string {
    let hash = 0;
    for (let i = 0; i < userId.length; i += 1) {
      hash = (hash + userId.charCodeAt(i) * (i + 1)) % AVATAR_COLORS.length;
    }
    return AVATAR_COLORS[hash];
  }

  private formatTime(value: string | null): string {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const now = new Date();
    const sameDay =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    if (sameDay) {
      return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { weekday: 'short' });
  }
}
